import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MdExpandMore, MdExpandLess, MdPersonSearch, MdOpenInNew } from 'react-icons/md';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';
import PersonHoverCard from 'components/common/PersonHoverCard';
import { employeeDetailPath } from './orgChartHelpers';

/**
 * People with no manager and no reports.
 *
 * They are kept off the canvas on purpose: hanging a few hundred single nodes off
 * the company root would bury the actual hierarchy under a wall of one-node
 * columns. Listing them here keeps them visible — and the list is exactly HR's
 * backlog of employees whose manager was never set.
 *
 * Deactivated people are excluded: a leaver does not need a manager, so putting
 * them on that backlog is noise. The count is still stated, because a list that
 * quietly omits rows is worse than a longer one.
 */
const OrgChartUnassigned = ({ people, inactiveCount = 0 }) => {
  const [open, setOpen] = useState(false);

  if (!people.length && !inactiveCount) return null;

  return (
    <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50/60 tw-overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-transparent tw-border-0 tw-text-left"
      >
        <MdPersonSearch size={16} className="tw-text-amber-600 tw-shrink-0" />
        <span className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-amber-800">
          No manager assigned
        </span>
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-5 tw-min-w-[20px] tw-px-1.5 tw-rounded-xl tw-bg-amber-500 tw-text-white tw-text-[11px] tw-font-bold">
          {people.length}
        </span>
        {inactiveCount > 0 && (
          <span className="tw-text-[11px] tw-font-medium tw-text-amber-700/70">
            ({inactiveCount} deactivated not listed)
          </span>
        )}
        <span className="tw-ml-auto tw-text-amber-700">
          {open ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
        </span>
      </button>

      {open && (
        <div className="tw-px-4 tw-pb-3 tw-flex tw-flex-wrap tw-gap-2">
          {people.map((person) => (
            <PersonHoverCard key={person.id} person={person}>
              <a
                href={employeeDetailPath(person.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-rounded tw-border tw-border-solid tw-border-amber-200 tw-bg-white tw-no-underline hover:tw-border-amber-400 tw-transition-colors"
              >
                <EmployeeAvatar name={person.name} size={24} fontSize={10} />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
                  {person.full_name || person.name}
                </span>
                <MdOpenInNew size={10} className="tw-text-slate-300" />
              </a>
            </PersonHoverCard>
          ))}
        </div>
      )}
    </div>
  );
};

OrgChartUnassigned.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  inactiveCount: PropTypes.number,
};

export default OrgChartUnassigned;
